import {isRejected, ThunkMiddleware} from '@reduxjs/toolkit'

import {changeLocation, LocationChangeAction} from '@web/actions/application'
import {isApiAsyncThunkMaintenanceRejected} from '@web/helpers/api'
import {browserAlert} from '@web/middlewares/browserEventEmitter'

import {parseServiceUnavailableError} from './helpers'

let isAlreadyInterrupted = false

const DefaultMessage = `안정적인 서비스 제공을 위하여 현재 서비스 점검이 진행되고 있습니다. 서비스 이용에 불편을 드려 죄송합니다.
Our service is currently undergoing maintenance to ensure stability. We apologize for any inconvenience.`

const MessageMap: Record<string, string> = {
    // 결제형의 경우 서버에서 내려온 점검 메시지 통해 얼럿 노출
    // - MAINTENANCE_ONGOING
    // - MaintenanceOngoing
}

type MaintenanceGatewayErrorResponse = {
    code: string
    message: string
    body: {code?: string; message?: string}
}

const maintenanceHandler =
    <RootState>({
        resolver,
    }: {
        resolver?: ({
            next,
            state,
            response,
        }: {
            next: (action: unknown) => unknown
            state: RootState
            response: MaintenanceGatewayErrorResponse
        }) => void
    } = {}): ThunkMiddleware<RootState> =>
    ({getState}) =>
    (next) =>
    (action) => {
        if (!isRejected(action)) {
            return next(action)
        }

        if (isApiAsyncThunkMaintenanceRejected<MaintenanceGatewayErrorResponse>(action)) {
            const parsedServiceUnavailableError: MaintenanceGatewayErrorResponse = action.payload.value.response.result
            /**
             * 점검시 BFF에서 503/529 status를 503 status로 mapping
             * 503 status 응답시 임시로 alert 후 back/close 처리 (재해복구 훈련 대응으로 추후 변경)
             * @see https://oss.fin.navercorp.com/payment-fe/order-web/issues/19313#issuecomment-2110648
             */

            if (isAlreadyInterrupted) {
                return
            }

            isAlreadyInterrupted = true

            if (resolver) {
                resolver({next, state: getState(), response: parsedServiceUnavailableError})
            } else {
                next(
                    browserAlert({
                        message:
                            // 1. 코드가 서버에서 내려올 경우 코드를 통해 메시지 맵핑
                            // 2. 메시지가 서버에서 내려올 경우 메시지 그대로 사용
                            // 3. 만약 코드/메시지 모두 없을 경우 FE 기본 점검 메시지 사용

                            // 결제형: 서버에서 body에 message를 내려줌 (점검시간 포함된 메시지) (2)
                            // 주문형: 서버에서 message를 내려주지 않아서 FE(web)에서 하드코딩 (3)
                            MessageMap[parsedServiceUnavailableError.code ?? ''] ||
                            parsedServiceUnavailableError.body.message ||
                            DefaultMessage,
                    }),
                )
                next(changeLocation({type: LocationChangeAction.BACK, close: true}))
            }
        }

        /**
         * @deprecated
         * createApiAsyncThunk 로 전환되면 아래 코드 제거
         **/
        if (!action.error.message) {
            return next(action)
        }

        const parsedServiceUnavailableError = parseServiceUnavailableError(action.error.message)

        if (parsedServiceUnavailableError) {
            /**
             * 점검시 BFF에서 503/529 status를 503 status로 mapping
             * 503 status 응답시 임시로 alert 후 back/close 처리 (재해복구 훈련 대응으로 추후 변경)
             * @see https://oss.fin.navercorp.com/payment-fe/order-web/issues/19313#issuecomment-2110648
             */

            if (isAlreadyInterrupted) {
                return
            }

            isAlreadyInterrupted = true

            if (resolver) {
                resolver({next, state: getState(), response: parsedServiceUnavailableError})
            } else {
                next(
                    browserAlert({
                        message:
                            // 1. 코드가 서버에서 내려올 경우 코드를 통해 메시지 맵핑
                            // 2. 메시지가 서버에서 내려올 경우 메시지 그대로 사용
                            // 3. 만약 코드/메시지 모두 없을 경우 FE 기본 점검 메시지 사용

                            // 결제형: 서버에서 body에 message를 내려줌 (점검시간 포함된 메시지) (2)
                            // 주문형: 서버에서 message를 내려주지 않아서 FE(web)에서 하드코딩 (3)
                            MessageMap[parsedServiceUnavailableError.code ?? ''] ||
                            parsedServiceUnavailableError.body.message ||
                            DefaultMessage,
                    }),
                )
                next(changeLocation({type: LocationChangeAction.BACK, close: true}))
            }
        }
        /**
         * @end
         **/

        /**
         * 점검오류 아닐경우 통과
         */
        return next(action)
    }

export default maintenanceHandler
